import { DocumentDuplicateIcon as DocumentDuplicateIconOutline } from "@heroicons/react/24/outline";
import { DocumentDuplicateIcon as DocumentDuplicateIconSolid } from "@heroicons/react/24/solid";
import _ from "lodash";
import type React from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { useWindowSize } from "usehooks-ts";
import Avatar from "@/atoms-react/avatar/Avatar.react";
import { Base, SectionTitle } from "@/atoms-react/text/Text.react";
import { useAuth } from "@/features/auth/hooks/use-auth-react";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { companyDetails } from "@/features/company/types";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { NaturalPerson, UserType } from "@/features/user/types";
import { getFullName } from "@/features/utils/functions";
import {
  TAILWIND_BREAKPOINTS,
  TAILWIND_BREAKPOINTS_WIDTH
} from "@/utils/static-data/tailwind-breakpoints";

type IdentityCardProps = {
  user?: UserType;
  companyDetails?: companyDetails | null;
  id?: string | null;
} & Omit<React.ComponentPropsWithoutRef<"div">, "id">;

export const IdentityHeaderCard = ({ user, companyDetails, id, ...props }: IdentityCardProps) => {
  const { cached } = useAuth();
  const origin = useOrigin();
  const { t } = useTranslation(["pass"]);
  const [copied, setCopied] = useState(false);
  const naturalPersonMode = true;
  const { leUuid } = useCompanyContext();
  const { width } = useWindowSize();
  const copyToClipboard = () => {
    const copyID = leUuid ? companyDetails?.legalEntity.identityUuid : user?.naturalPerson.UUID;

    if (copyID) {
      navigator?.clipboard
        ?.writeText(copyID)
        .then(() => {
          setCopied(true);
          toast.success(`${t("olkypass.accountPage.basicInfo.success.copied_id")}`);
          setTimeout(() => setCopied(false), 1000);
        })
        .catch((e) => console.log(e));
    }
  };

  return (
    <div
      className={twMerge("relative overflow-hidden", props.className)}
      {..._.omit(props, "className")}
    >
      <div className="absolute z-0 h-12 w-full bg-[url('/images/olkypass/olky-space-background.jpg')] bg-center bg-cover bg-no-repeat md:h-20"></div>

      {!leUuid
        ? naturalPersonMode && (
            <div className="relative z-10 px-4 pt-4 pb-2 md:px-6 md:pt-6 md:pb-0">
              <Avatar
                className="border-4 border-white md:mb-3"
                size={width > TAILWIND_BREAKPOINTS_WIDTH[TAILWIND_BREAKPOINTS.MD] ? 24 : 16}
                fallback={cached?.name}
              ></Avatar>
            </div>
          )
        : companyDetails?.legalEntity.LegalName && (
            <div className="relative z-10 px-4 pt-4 pb-2 md:px-6 md:pt-6 md:pb-0">
              <Avatar
                className="border-4 border-white md:mb-3"
                size={width > TAILWIND_BREAKPOINTS_WIDTH[TAILWIND_BREAKPOINTS.MD] ? 24 : 16}
                fallback={companyDetails?.legalEntity.LegalName}
              ></Avatar>
            </div>
          )}
      <div className="flex flex-col px-6 md:justify-between md:pb-5">
        <div className="mb-4 flex flex w-full flex-col justify-between md:mb-0 md:flex-row">
          <div className="roun flex items-center md:mb-1">
            <SectionTitle>
              {leUuid
                ? companyDetails?.legalEntity.LegalName
                : getFullName(user?.naturalPerson as NaturalPerson)}
            </SectionTitle>
          </div>
          <span className="flex flex-col md:flex-row">
            <div className="flex flex-col text-left font-semibold md:text-right md:text-lg">
              <Base className="dark:text-white">
                
                {leUuid
                  ? t("olkypass.accountPage.profileSection.label.kyc_identifier_pro")
                  : t("olkypass.accountPage.profileSection.label.kyc_identifier")}
                :
              </Base>
              <div className="flex flex-row content-end text-center md:mt-2 md:text-right">
                <Base
                  className={twMerge(
                    "mr-2 truncate rounded-md bg-blue-100 px-3 py-1 text-primary dark:text-primary",
                    origin !== FromURL.None && "bg-primary_menu_item"
                  )}
                >
                  {leUuid ? companyDetails?.legalEntity.identityUuid : user?.naturalPerson.UUID}
                </Base>
                <div className="flex flex-col justify-center">
                  {copied ? (
                    <div className="relative flex items-center">
                      <DocumentDuplicateIconSolid
                        className="h-5 w-5 cursor-pointer stroke-2 text-primary hover:text-blue-600"
                        onClick={copyToClipboard}
                      />
                    </div>
                  ) : (
                    <DocumentDuplicateIconOutline
                      className="h-5 w-5 cursor-pointer stroke-2 text-primary hover:text-blue-600"
                      onClick={copyToClipboard}
                    />
                  )}
                </div>
              </div>
            </div>
          </span>
        </div>
      </div>
    </div>
  );
};
